import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import type { InnerCircleGroup, InnerCircleMember, SharedItem, ChatMessage } from '../types/inner-circle';

export function useInnerCircleGroups() {
  const { user } = useAuthContext();
  const [groups, setGroups] = useState<InnerCircleGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user) {
      fetchGroups();
      
      const subscription = supabase
        .channel('inner_circle_changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'inner_circle_groups' },
          fetchGroups
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('inner_circle_groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setGroups(data.map(group => ({
        ...group,
        created_at: new Date(group.created_at),
        updated_at: new Date(group.updated_at)
      })));
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createGroup = async (data: {
    name: string;
    type: InnerCircleGroup['type'];
    description?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('inner_circle_groups')
        .insert({
          user_id: user?.id,
          ...data
        });

      if (error) throw error;
      await fetchGroups();
    } catch (err) {
      console.error('Error creating group:', err);
      throw err;
    }
  };

  const inviteMember = async (
    groupId: string,
    email: string,
    role: InnerCircleMember['role']
  ) => {
    try {
      // First find the user by email
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (userError || !userData) throw new Error('User not found');

      const { error } = await supabase
        .from('inner_circle_members')
        .insert({
          group_id: groupId,
          user_id: userData.id,
          role,
          status: 'pending'
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error inviting member:', err);
      throw err;
    }
  };

  const shareItem = async (
    groupId: string,
    type: SharedItem['type'],
    itemId: string
  ) => {
    try {
      const { error } = await supabase
        .from('inner_circle_shared_items')
        .insert({
          group_id: groupId,
          user_id: user?.id,
          type,
          item_id: itemId
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error sharing item:', err);
      throw err;
    }
  };

  const sendMessage = async (
    groupId: string,
    content: string,
    type: ChatMessage['type'] = 'text',
    fileUrl?: string
  ) => {
    try {
      const { error } = await supabase
        .from('inner_circle_chat')
        .insert({
          group_id: groupId,
          user_id: user?.id,
          content,
          type,
          file_url: fileUrl
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  return {
    groups,
    loading,
    error,
    createGroup,
    inviteMember,
    shareItem,
    sendMessage
  };
}